/**
 * @typedef {import('@floating-ui/dom').VirtualElement} VirtualAnchor
 */

/**
 * @typedef {Element | VirtualAnchor} FloatingAnchor
 */

/**
 * @typedef {object} AnchorEdges
 * @property {number} top - Top edge in viewport coordinates
 * @property {number} left - Left edge in viewport coordinates
 * @property {number} bottom - Bottom edge in viewport coordinates
 * @property {number} right - Right edge in viewport coordinates
 */

/**
 * An anchor for something that is not an element of its own, such as a text
 * range. The edges are read again on every position update, so the floating
 * element follows the range through scrolling and edits. The context element
 * is where scroll ancestors are found; without it only window scroll is seen.
 * @param {() => AnchorEdges} getEdges Current edges of the anchored range
 * @param {Element} contextElement Element the range lives inside
 * @returns {VirtualAnchor} Virtual anchor
 */
export function virtualAnchor(getEdges, contextElement) {
  return {
    contextElement,
    getBoundingClientRect() {
      const { top, left, bottom, right } = getEdges();
      return {
        x: left,
        y: top,
        top,
        left,
        bottom,
        right,
        width: right - left,
        height: bottom - top,
      };
    },
  };
}

/**
 * The element a floating element should be mounted into. An open dialog sits
 * in the top layer, so anything mounted outside it renders behind the dialog.
 * The dialog itself is the host rather than its box, because a DaisyUI modal
 * box sets `scale` and `translate`, which make it the containing block of
 * fixed descendants and let its scrolling overflow clip them.
 * @param {FloatingAnchor | null | undefined} anchor What the element floats next to
 * @returns {Element} Mount point
 */
export function floatingHost(anchor) {
  const element = anchor instanceof Element ? anchor : anchor?.contextElement;
  return element?.closest("dialog") ?? document.body;
}
